import { IdSchema } from "@pax/world";
import { z } from "zod";
import { AgentVersionId, GameId, GameKind, UserId } from "../../common";
import { GameMember, GameMode, GameRole } from "../../games/v1alpha1/schemas";
import {
  ModerationCategory,
  ModerationWarning,
  SurfaceBan,
} from "../../moderation/v1alpha1/schemas";

export const TelemetrySignal = z.enum(["logs", "traces"]);
export type TelemetrySignal = z.infer<typeof TelemetrySignal>;

export const GatewayRequestHeaders = z.strictObject({
  "x-pax-game-id": GameId,
  "x-pax-game-kind": z.enum(["play", "eval"]),
  "x-pax-agent-id": z.uuid(),
});
export type GatewayRequestHeaders = z.infer<typeof GatewayRequestHeaders>;

/**
 * Set to `no` on every model call response the API produces without contacting the vendor.
 * The API strips it from vendor responses, so only the API can claim it.
 */
export const PROVIDER_CONTACTED_HEADER = "x-pax-provider-contacted";
export const PROVIDER_NOT_CONTACTED = "no";

const Sha256Digest = z.string().regex(/^[a-f0-9]{64}$/);

export const Sha256BlobParams = z.strictObject({
  sha256: Sha256Digest,
});
export type Sha256BlobParams = z.infer<typeof Sha256BlobParams>;

export const AgentVersionParams = z.strictObject({
  agentVersionId: AgentVersionId,
});
export type AgentVersionParams = z.infer<typeof AgentVersionParams>;

const Entrypoint = z
  .string()
  .regex(/^[a-zA-Z0-9][a-zA-Z0-9._/-]*$/)
  .refine(path => !path.split("/").includes(".."), "entrypoint may not traverse directories");

export const CatalogObjectPin = z.object({
  digest: Sha256Digest.meta({
    description: "SHA-256 of the exact object bytes.",
  }),
  bytes: z.number().int().positive().meta({
    description: "Exact object byte length.",
  }),
});
export type CatalogObjectPin = z.infer<typeof CatalogObjectPin>;

/** Where one published agent version's code is, and the file to run: what the agent runner needs to start it. */
export const AgentVersionPin = z.object({
  agentId: z.uuid(),
  agentVersionId: z.uuid(),
  entrypoint: Entrypoint,
  bundle: CatalogObjectPin,
});
export type AgentVersionPin = z.infer<typeof AgentVersionPin>;

/** A member's ban from live games, while it lasts, and whether they can appeal it. */
export const LiveBan = SurfaceBan.pick({
  id: true,
  appealable: true,
  appeal: true,
  startsAt: true,
  endsAt: true,
});
export type LiveBan = z.infer<typeof LiveBan>;

/** What a game's session reads from the API: the game's kind, its preset, and everyone in it. */
export const SessionGame = z.strictObject({
  kind: GameKind,
  mode: GameMode,
  presetId: z.uuid(),
  presetVersionId: z.uuid().meta({ description: "The preset version the game was made from." }),
  members: z.array(GameMember).meta({ description: "The owner first, then by when they joined." }),
  bans: z.array(z.strictObject({ userId: UserId, ban: LiveBan })).meta({
    description:
      "The members banned from live games right now. They stay members, but only spectate, and can't connect or change anything, until their ban ends.",
  }),
  standInAgentVersionId: AgentVersionId.nullable().meta({
    description:
      "The player agent version that plays a perspective while everyone playing it is offline: the published version of the catalog's first player agent. Null without one.",
  }),
});
export type SessionGame = z.infer<typeof SessionGame>;

/** A grader or player agent the game started that stopped without finishing what it was started for. */
export const RecordAgentFailureRequest = z.strictObject({
  agentId: z.uuid(),
  error: z.string().min(1).max(20_000),
});
export type RecordAgentFailureRequest = z.infer<typeof RecordAgentFailureRequest>;

export const RecordAgentFailureResponse = z.strictObject({
  recorded: z
    .boolean()
    .meta({ description: "Whether a trial of the game was still waiting on it." }),
});
export type RecordAgentFailureResponse = z.infer<typeof RecordAgentFailureResponse>;

export const GameAgentStateParams = z.strictObject({
  id: GameId,
  agentId: z.uuid().meta({ description: "The agent's id in the game's World." }),
});
export type GameAgentStateParams = z.infer<typeof GameAgentStateParams>;

export const GameParams = z.strictObject({
  id: GameId,
});
export type GameParams = z.infer<typeof GameParams>;

export const GameMemberParams = z.strictObject({
  id: GameId,
  userId: IdSchema,
});
export type GameMemberParams = z.infer<typeof GameMemberParams>;

/** The perspective one member plays, or null for a spectator. */
export const MemberCasting = z.strictObject({
  perspective: z
    .strictObject({
      handle: IdSchema,
      name: z.string().min(1).meta({ description: "Its alias in its own view." }),
      icon: z.uuid().nullable().meta({ description: "The image asset its alias shows it by." }),
    })
    .nullable(),
});
export type MemberCasting = z.infer<typeof MemberCasting>;

/** Which perspective one member plays. */
export const Casting = MemberCasting.extend({ userId: IdSchema });
export type Casting = z.infer<typeof Casting>;

/** The agent version holding the game master role. `agentVersionId` is null while nobody does. */
export const GameMasterCasting = z.strictObject({
  agentVersionId: z.uuid().nullable(),
});
export type GameMasterCasting = z.infer<typeof GameMasterCasting>;

export const RecordCheckpointRequest = z.strictObject({
  revision: z.number().int().positive(),
  roundsCompleted: z
    .number()
    .int()
    .nonnegative()
    .meta({ description: "How many rounds the world has completed at that revision." }),
  casting: z.array(Casting).meta({
    description:
      "The perspective of everyone on one at that revision. Members not listed are on none.",
  }),
  gameMasterAgentVersionId: GameMasterCasting.shape.agentVersionId.meta({
    description: "The agent version holding the game master role at that revision.",
  }),
});
export type RecordCheckpointRequest = z.infer<typeof RecordCheckpointRequest>;

export const RecordCheckpointResponse = z.strictObject({
  savedRevision: z.number().int().positive(),
  departedUserIds: z.array(IdSchema).meta({
    description: "Users in the casting who are no longer members of the game.",
  }),
});
export type RecordCheckpointResponse = z.infer<typeof RecordCheckpointResponse>;

export const RecordCastingResponse = z.strictObject({
  recorded: z.boolean().meta({ description: "Whether the user is still a member of the game." }),
});
export type RecordCastingResponse = z.infer<typeof RecordCastingResponse>;

/**
 * Who runs a game, as its lobby holds it: the owner, the roles, and who holds which. The game's
 * session keeps it and its history, and the API keeps a copy to check permissions without waking it.
 */
export const LobbyCopy = z.strictObject({
  revision: z.number().int().positive().meta({
    description:
      "The lobby revision of the latest change this state holds. The API keeps the newest it is sent.",
  }),
  ownerId: UserId,
  roles: z.array(GameRole),
  memberRoles: z.array(z.strictObject({ userId: UserId, roleIds: z.array(IdSchema) })).meta({
    description:
      "The roles each member holds beyond the everyone role. Members not listed hold none.",
  }),
});
export type LobbyCopy = z.infer<typeof LobbyCopy>;

export const RecordLobbyResponse = z.strictObject({
  recorded: z.boolean().meta({
    description:
      "False when the game is gone, or the API already holds this revision or a newer one.",
  }),
});
export type RecordLobbyResponse = z.infer<typeof RecordLobbyResponse>;

export const RecordLiveRequest = z.strictObject({
  live: z.boolean().meta({ description: "Whether any connection to the game is open." }),
});
export type RecordLiveRequest = z.infer<typeof RecordLiveRequest>;

export const RecordLiveResponse = z.strictObject({
  recorded: z.boolean().meta({ description: "False when the game is gone." }),
});
export type RecordLiveResponse = z.infer<typeof RecordLiveResponse>;

/** When a member left the game: the moment their last connection ended. */
export const RecordLastOnlineRequest = z.strictObject({
  at: z.iso.datetime({ offset: true }),
});
export type RecordLastOnlineRequest = z.infer<typeof RecordLastOnlineRequest>;

export const RecordLastOnlineResponse = z.strictObject({
  recorded: z.boolean().meta({ description: "Whether the user is still a member of the game." }),
});
export type RecordLastOnlineResponse = z.infer<typeof RecordLastOnlineResponse>;

/** Whether the account a sign-in cookie belongs to may connect to a game. */
export const PlayerAdmission = z.discriminatedUnion("status", [
  z.strictObject({
    status: z.literal("admitted"),
    userId: UserId.meta({
      description: "The cookie's account, which is its identity in the world.",
    }),
  }),
  z
    .strictObject({ status: z.literal("signedOut") })
    .meta({ description: "The cookie carries no session." }),
  z
    .strictObject({
      status: z.literal("banned"),
      ban: SurfaceBan.nullable().meta({
        description: "The account's ban from live games, or null when the whole account is banned.",
      }),
    })
    .meta({ description: "The account is banned from live games, or from Pax altogether." }),
  z
    .strictObject({ status: z.literal("notMember") })
    .meta({ description: "The game does not exist or the account is not a member." }),
]);
export type PlayerAdmission = z.infer<typeof PlayerAdmission>;

export const RecordGameMasterResponse = z.strictObject({
  recorded: z.boolean().meta({ description: "Whether the game exists." }),
});
export type RecordGameMasterResponse = z.infer<typeof RecordGameMasterResponse>;

export const PutAgentStateResponse = z.strictObject({
  bytes: z.number().int().nonnegative(),
});
export type PutAgentStateResponse = z.infer<typeof PutAgentStateResponse>;

export const LiveInputKind = z.enum(["chat", "action", "rule", "role", "title"]).meta({
  id: "LiveInputKind",
  description:
    "What a player wrote in a live game: `chat`, a message, a poll's question and options, or a chat's title; `action`, what they have their perspective do, guidance for a jump, an answer in their own words to the game master, or a perspective of their own; `rule`, one of the game's rules; `role`, a role's name; `title`, the game's title.",
});
export type LiveInputKind = z.infer<typeof LiveInputKind>;

/** The longest input moderation judges. */
export const LIVE_INPUT_MAX_LENGTH = 20_000;

export const JudgeLiveInputRequest = z.strictObject({
  gameId: GameId,
  inputId: IdSchema.meta({
    description:
      "The caller's id for the input, such as the proposal and position it came in, which moderators find it by. Every request is judged, even one that repeats an earlier id.",
  }),
  authorId: z.uuid().meta({ description: "The player who wrote it, a member of the game." }),
  kind: LiveInputKind,
  text: z.string().trim().min(1).max(LIVE_INPUT_MAX_LENGTH).meta({
    description:
      "The text exactly as other players would see it. A poll is its question and options together; a rule its name, description, and instructions.",
  }),
});
export type JudgeLiveInputRequest = z.infer<typeof JudgeLiveInputRequest>;

const LiveStanding = z
  .strictObject({
    ban: LiveBan.nullable().meta({
      description:
        "The author's ban from live games in force, or null. While it lasts they only spectate.",
    }),
    nearBan: z.boolean().meta({
      description: "Whether the author is close to a ban from live games, while not banned.",
    }),
  })
  .meta({ id: "LiveStanding", description: "Where the author stands on live games." });

const classificationId = z.uuid().meta({
  description: "Automod's classification of the input, for a moderator to look up.",
});

export const JudgeLiveInputResponse = z
  .discriminatedUnion("outcome", [
    z.strictObject({
      outcome: z.literal("pass"),
      classificationId,
      standing: LiveStanding,
    }),
    z.strictObject({
      outcome: z.literal("redact"),
      classificationId,
      warning: ModerationWarning.meta({
        description:
          "How to tell the author: `small` the inline warning, `big` the warning pop-up, `crisis_resources` the welfare message with crisis support links.",
      }),
      categories: z.array(ModerationCategory).min(1).meta({
        description: "The categories that caused the redaction, most severe first.",
      }),
      points: z.number().int().nonnegative().meta({
        description: "The points the input added toward a ban from live games.",
      }),
      standing: LiveStanding,
    }),
    z.strictObject({
      outcome: z.literal("banned"),
      standing: LiveStanding,
    }),
    z.strictObject({ outcome: z.literal("unavailable") }),
  ])
  .meta({
    id: "JudgeLiveInputResponse",
    description:
      "`pass`: show the input. `redact`: never show it. `banned`: the author is banned from live games, so the input wasn't judged and is never shown. `unavailable`: automod couldn't judge it, so it is never shown and can be sent again.",
  });
export type JudgeLiveInputResponse = z.infer<typeof JudgeLiveInputResponse>;
